import { ReactElement, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { isNil } from 'lodash';

import useWorkflowsStore from 'models/workflows/workflows.store';
import { PageLoading } from 'components/loading';
import { getFirstMenuItemUrl } from 'utils/workflows';
import { findCustomFlowApp } from 'utils/formHost/formHost.helpers';
import { MenuAppLike } from 'utils/formHost/bridgeMessages';
import FormHost from './FormHost';

/**
 * Renders FormHost only when the user has access to the flow: a CUSTOM_FLOW
 * app whose marketplaceKey matches the route. Mirrors WorkflowsPrivateView's
 * lookup. The backend already filters the lists, so no extra role checks.
 */
const CustomFlowAccessGuard = (): ReactElement => {
  const navigate = useNavigate();
  const { flowId } = useParams();
  const { dynamicMenu, privateApps } = useWorkflowsStore();

  // Lists start as null until fetched; treat null as "not fetched yet".
  const fetched = !isNil(dynamicMenu) && !isNil(privateApps);

  const flowApp = useMemo(() => {
    if (!fetched) return undefined;
    const apps: MenuAppLike[] = [...(dynamicMenu || []), ...(privateApps || [])];
    return findCustomFlowApp(apps, flowId);
  }, [fetched, dynamicMenu, privateApps, flowId]);

  const granted = fetched && Boolean(flowApp);
  const denied = fetched && !flowApp;

  useEffect(() => {
    if (denied) {
      navigate(getFirstMenuItemUrl(dynamicMenu), { replace: true });
    }
  }, [denied, navigate, dynamicMenu]);

  if (!granted) {
    return <PageLoading />;
  }

  return <FormHost />;
};

export default CustomFlowAccessGuard;
